import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Battery, Sun, ArrowRight, Zap, MapPin } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import MeshBackgroundVideo from '@/components/MeshBackgroundVideo';
import { reopenConsent } from '@/lib/consent';
import NetzdienlichStempel from '@/components/NetzdienlichStempel';
import VisitCounter from '@/components/VisitCounter';

const MainLanding = () => {
  const { t, i18n } = useTranslation();
  const [content, setContent] = useState({
    company: {
      name: 'Speier Solarbetriebe',
      tagline: 'Ihr Partner für nachhaltige Energieinvestitionen'
    },
    hero: {
      title: 'Investieren Sie in die Energiezukunft Deutschlands',
      subtitle: 'Profitieren Sie von attraktiven Renditen durch nachhaltige Energie-Infrastrukturprojekte'
    },
    bess: {
      title: 'BESS Projekte',
      description: 'Battery Energy Storage Systems – Investieren Sie in die Zukunft der Energiespeicherung',
      cta: 'Projekte entdecken',
      badge1: '8-9% Rendite p.a.',
      badge2: '10 Jahre Laufzeit',
      badge3: 'Nachhaltig & Zukunftssicher'
    },
    pv: {
      title: 'PV-Anlagenverwaltung',
      description: 'Photovoltaik-Anlagenverwaltung – Professionelles Management Ihrer Solarinvestitionen',
      status: 'Demnächst verfügbar',
      badge1: 'Monitoring & Reporting',
      badge2: 'Wartungsmanagement',
      badge3: 'Ertragsoptimierung'
    },
    features: {
      feature1_title: 'Nachhaltig',
      feature1_text: 'Investitionen in erneuerbare Energien und Energiespeicherung für Sicherheit für die nächste Generation',
      feature2_title: 'Sicher',
      feature2_text: 'Geprüfte Projekte mit transparenten Geschäftsmodellen und professioneller Verwaltung',
      feature3_title: 'Rentabel',
      feature3_text: 'Attraktive Renditen durch etablierte Technologien und langfristige Verträge'
    }
  });

  useEffect(() => {
    fetchContent();
  }, [i18n.language]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/homepage`, { params: { lang: i18n.language } });
      if (response.data.content && Object.keys(response.data.content).length > 0) {
        setContent(response.data.content);
      }
    } catch (error) {
      console.error('Error fetching homepage content:', error);
      // Use default content if fetch fails
    }
  };

  return (
    <div className="relative isolate min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="main-landing-page">
      <VisitCounter />
      <MeshBackgroundVideo />
      {/* Header with Company Name */}
      <header className="py-8 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
          <LanguageSwitcher />
        </div>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center space-x-3">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-2xl">BA</span>
            </div>
            <div className="text-center">
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                {content.company?.name || 'Speier Solarbetriebe'}
              </h1>
              <p className="text-slate-300 text-sm mt-1">
                {content.company?.tagline || 'Ihr Partner für nachhaltige Energieinvestitionen'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            <span className="bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
              {content.hero?.title || 'Investieren Sie in die Energiezukunft Deutschlands'}
            </span>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            {content.hero?.subtitle || 'Profitieren Sie von attraktiven Renditen durch nachhaltige Energie-Infrastrukturprojekte'}
          </p>
        </div>

        {/* Two Main Buttons */}
        <div className="max-w-7xl mx-auto">
          {/* Breiter Rechner-Button über beiden Themen (gilt für BESS und PV/Hyper-Grid) */}
          <a
            href="/speicher-rechner/"
            className="cta-pulse group mb-8 flex w-full items-center justify-center gap-4 rounded-full bg-gradient-to-r from-green-500 via-emerald-500 to-orange-500 px-8 py-5 text-center text-white shadow-2xl"
            data-testid="rechner-button"
          >
            <Zap size={28} className="shrink-0" aria-hidden="true" />
            <span className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4">
              <span className="text-xl font-bold sm:text-2xl">{t('mainLanding.rechnerTitle')}</span>
              <span className="text-sm text-white/90">{t('mainLanding.rechnerSub')}</span>
            </span>
            <ArrowRight size={26} className="shrink-0 transition-transform group-hover:translate-x-2" aria-hidden="true" />
          </a>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* BESS Projects Button */}
            <Link to="/bess" className="group" data-testid="bess-button">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl transition-all duration-500 hover:scale-105 hover:shadow-green-500/50">
                <div className="absolute inset-0 bg-gradient-to-br from-green-600/20 to-blue-600/20 group-hover:from-green-600/40 group-hover:to-blue-600/40 transition-all duration-500" />
                
                <img
                  src={content.bess?.image || "https://images.unsplash.com/photo-1578776349090-de61da00ff1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxwb3dlciUyMHBsYW50fGVufDB8fHx8MTc2MDI1OTMwMXww&ixlib=rb-4.1.0&q=85"}
                  alt="BESS Projekte"
                  className="w-full h-96 object-cover"
                />
                
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent" />
                
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Battery className="text-white" size={40} />
                  </div>
                  
                  <h3 className="text-4xl font-bold text-white mb-4">
                    {content.bess?.title || 'BESS Projekte'}
                  </h3>
                  
                  <p className="text-lg text-slate-200 mb-6 max-w-md">
                    {content.bess?.description || 'Battery Energy Storage Systems – Investieren Sie in die Zukunft der Energiespeicherung'}
                  </p>
                  
                  <div className="flex items-center text-green-400 font-semibold group-hover:text-white transition-colors">
                    <span className="mr-2">{content.bess?.cta || 'Projekte entdecken'}</span>
                    <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform" />
                  </div>
                  
                  <div className="mt-6 flex flex-wrap gap-3 justify-center">
                    {content.bess?.badge1 && (
                      <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white border border-white/20">
                        {content.bess.badge1}
                      </span>
                    )}
                    {content.bess?.badge2 && (
                      <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white border border-white/20">
                        {content.bess.badge2}
                      </span>
                    )}
                    {content.bess?.badge3 && (
                      <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white border border-white/20">
                        {content.bess.badge3}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Link>

            {/* HYPER GRID / PV Management Button */}
            <Link to="/hyper-grid" className="group relative" data-testid="pv-button">
              <NetzdienlichStempel className="absolute -right-3 -top-6 z-20 sm:-right-4 sm:-top-7" size="md" delay={0.8} />
              <div className="relative overflow-hidden rounded-2xl shadow-2xl transition-all duration-500 opacity-90 hover:opacity-100">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-600/20 to-orange-600/20 group-hover:from-yellow-600/30 group-hover:to-orange-600/30 transition-all duration-500" />
                
                <img
                  src={content.pv?.image || "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxyZW5ld2FibGUlMjBlbmVyZ3l8ZW58MHx8fHwxNzYwMjU5MzA2fDA&ixlib=rb-4.1.0&q=85"}
                  alt="PV-Anlagenverwaltung"
                  className="w-full h-96 object-cover"
                />
                
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent" />
                
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Sun className="text-white" size={40} />
                  </div>
                  
                  <h3 className="text-4xl font-bold text-white mb-4">
                    {content.pv?.title || 'PV-Anlagenverwaltung'}
                  </h3>

                  <span className="btn-vibrate mb-4 inline-block rounded-full bg-green-500 px-5 py-1.5 text-base font-bold uppercase tracking-wide text-slate-900 shadow-lg" data-testid="grid-operator-badge">
                    {t('mainLanding.gridOperator')}
                  </span>
                  
                  <p className="text-lg text-slate-200 mb-6 max-w-md">
                    {content.pv?.description || 'Photovoltaik-Anlagenverwaltung – Professionelles Management Ihrer Solarinvestitionen'}
                  </p>
                  
                  <div className="inline-flex items-center px-6 py-3 bg-slate-700/50 backdrop-blur-sm rounded-full text-slate-300 border border-slate-600">
                    <span className="mr-2">{content.pv?.status || 'Demnächst verfügbar'}</span>
                  </div>
                  
                  <div className="mt-6 flex flex-wrap gap-3 justify-center opacity-70">
                    {content.pv?.badge1 && (
                      <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white border border-white/20">
                        {content.pv.badge1}
                      </span>
                    )}
                    {content.pv?.badge2 && (
                      <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white border border-white/20">
                        {content.pv.badge2}
                      </span>
                    )}
                    {content.pv?.badge3 && (
                      <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white border border-white/20">
                        {content.pv.badge3}
                      </span>
                    )}
                  </div>
                </div>

              </div>
            </Link>

          </div>
        </div>
      </section>

      {/* Features Section */}
      {/* Breiter Button unten: Fläche anbieten (gilt für BESS und PV/Hyper-Grid) */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/flaeche-anbieten"
            className="cta-pulse group flex w-full items-center justify-center gap-4 rounded-full bg-gradient-to-r from-orange-500 via-amber-500 to-green-500 px-8 py-5 text-center text-white shadow-2xl"
            data-testid="flaeche-button"
          >
            <MapPin size={28} className="shrink-0" aria-hidden="true" />
            <span className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4">
              <span className="text-xl font-bold sm:text-2xl">{t('mainLanding.flaecheTitle')}</span>
              <span className="text-sm text-white/90">{t('mainLanding.flaecheSub')}</span>
            </span>
            <ArrowRight size={26} className="shrink-0 transition-transform group-hover:translate-x-2" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {content.features?.feature1_title && (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Battery className="text-green-400" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{content.features.feature1_title}</h3>
                <p className="text-slate-400">
                  {content.features.feature1_text}
                </p>
              </div>
            )}

            {content.features?.feature2_title && (
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="32" height="32">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{content.features.feature2_title}</h3>
                <p className="text-slate-400">
                  {content.features.feature2_text}
                </p>
              </div>
            )}

            {content.features?.feature3_title && (
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="32" height="32">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{content.features.feature3_title}</h3>
                <p className="text-slate-400">
                  {content.features.feature3_text}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-slate-700">
        <div className="max-w-7xl mx-auto text-center text-slate-400 text-sm">
          <p>{t('mainLanding.footerCopyright')}</p>
          <p className="mt-2">
            <Link to="/bess/impressum" className="hover:text-green-400 transition-colors">
              {t('mainLanding.impressum')}
            </Link>
            {" | "}
            <Link to="/bess/datenschutz" className="hover:text-green-400 transition-colors">
              {t('mainLanding.privacy')}
            </Link>
            {" | "}
            <button type="button" onClick={reopenConsent} className="hover:text-green-400 transition-colors" data-testid="mainlanding-cookie-settings">
              {t('consent.settings')}
            </button>
            {" | "}
            <a href="#" className="hover:text-green-400 transition-colors">
              {t('mainLanding.contact')}
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MainLanding;
